import { useCallback, useEffect, useRef, useState } from 'react';
import type { AuthFlow } from '@shared/utils/analytics/events';

export type RegisterHoldDecision = {
  shouldRenderSmsRegister: boolean;
  shouldHoldSmsRegister: boolean;
  shouldHoldEmailRegister: boolean;
  shouldHoldArkoseRegister: boolean;
};

type RegisterHoldInput = {
  enabled: boolean;
  authFlow: 'login' | 'signup';
  isSignupContext: boolean;
  isPhoneScreen: boolean;
  isSignupPage: boolean;
  isArkoseScreen: boolean;
  pendingPhone?: string;
  hasPendingSignupEmail: boolean;
  errorText?: string;
  arkoseHoldTimedOut: boolean;
};

export const hasPendingRegisterSubmission = (
  pendingPhone: string | undefined,
  hasPendingSignupEmail: boolean
): boolean => Boolean(pendingPhone || hasPendingSignupEmail);

export const getRegisterHoldDecision = ({
  enabled,
  authFlow,
  isSignupContext,
  isPhoneScreen,
  isSignupPage,
  isArkoseScreen,
  pendingPhone,
  hasPendingSignupEmail,
  errorText,
  arkoseHoldTimedOut,
}: RegisterHoldInput): RegisterHoldDecision => {
  const isSignup = enabled && authFlow === 'signup';
  const shouldRenderSmsRegister = isSignup && isPhoneScreen;
  const shouldHoldSmsRegister =
    shouldRenderSmsRegister && Boolean(pendingPhone);
  const hasPendingSubmission = hasPendingRegisterSubmission(
    pendingPhone,
    hasPendingSignupEmail
  );
  const shouldHoldEmailRegister =
    enabled && isSignupContext && isSignupPage && hasPendingSignupEmail;
  const shouldHoldArkoseRegister =
    enabled &&
    isSignupContext &&
    isArkoseScreen &&
    hasPendingSubmission &&
    !errorText &&
    !arkoseHoldTimedOut;

  return {
    shouldRenderSmsRegister,
    shouldHoldSmsRegister,
    shouldHoldEmailRegister,
    shouldHoldArkoseRegister,
  };
};

type RegisterHoldHookInput = {
  enabled: boolean;
  authFlow: AuthFlow;
  isSignupContext: boolean;
  isPhoneScreen: boolean;
  isSignupPage: boolean;
  isArkoseScreen: boolean;
  hasPendingSignupEmail: boolean;
  pendingSignupEmail?: string;
  setPendingSignupEmail: (email: string | undefined) => void;
  errorText?: string;
};

export const usePendingSignupEmail = () => {
  const [pendingSignupEmail, setPendingSignupEmail] = useState<string>();
  const pendingSignupEmailRef = useRef(false);
  const setPendingSignupEmailWithMirror = useCallback(
    (email: string | undefined): void => {
      pendingSignupEmailRef.current = Boolean(email);
      setPendingSignupEmail(email);
    },
    []
  );
  const getHasPendingSignupEmail = useCallback(
    (): boolean => pendingSignupEmailRef.current,
    []
  );

  return {
    pendingSignupEmail,
    hasPendingSignupEmail: Boolean(pendingSignupEmail),
    setPendingSignupEmail: setPendingSignupEmailWithMirror,
    getHasPendingSignupEmail,
  };
};

export const useRegisterHold = ({
  enabled,
  authFlow,
  isSignupContext,
  isPhoneScreen,
  isSignupPage,
  isArkoseScreen,
  hasPendingSignupEmail,
  pendingSignupEmail,
  setPendingSignupEmail,
  errorText,
}: RegisterHoldHookInput) => {
  const [pendingSmsPhone, setPendingSmsPhone] = useState<string>();
  const [phoneSubmitInFlight, setPhoneSubmitInFlight] = useState(false);
  const [autoSubmitPendingSmsPhone, setAutoSubmitPendingSmsPhone] =
    useState(false);
  const [autoSubmitPendingSignupEmail, setAutoSubmitPendingSignupEmail] =
    useState(false);
  const [arkoseHoldTimedOut, setArkoseHoldTimedOut] = useState(false);

  useEffect(() => {
    const shouldStart =
      enabled &&
      isArkoseScreen &&
      hasPendingRegisterSubmission(pendingSmsPhone, hasPendingSignupEmail) &&
      !errorText;
    if (!shouldStart) {
      setArkoseHoldTimedOut(false);
      return undefined;
    }
    setArkoseHoldTimedOut(false);
    const timeoutId = window.setTimeout(
      () => setArkoseHoldTimedOut(true),
      2000
    );
    return () => window.clearTimeout(timeoutId);
  }, [
    enabled,
    errorText,
    hasPendingSignupEmail,
    isArkoseScreen,
    pendingSmsPhone,
  ]);

  const decision = getRegisterHoldDecision({
    enabled,
    authFlow,
    isSignupContext: isSignupContext || hasPendingSignupEmail,
    isPhoneScreen,
    isSignupPage,
    isArkoseScreen,
    pendingPhone: pendingSmsPhone,
    hasPendingSignupEmail,
    errorText,
    arkoseHoldTimedOut,
  });
  const onScreenUpdate = (
    nextErrorText: string | undefined,
    options: {
      isPhoneScreen: boolean;
      isEmailOtpScreen: boolean;
      isArkoseScreen: boolean;
      isSignupPage: boolean;
      isReturningToSignupPage: boolean;
    }
  ): void => {
    if ((!options.isPhoneScreen && !options.isArkoseScreen) || nextErrorText) {
      setAutoSubmitPendingSmsPhone(false);
      setPhoneSubmitInFlight(false);
    }
    if (nextErrorText || options.isReturningToSignupPage) {
      setAutoSubmitPendingSignupEmail(false);
    }
    if (
      pendingSignupEmail &&
      !options.isEmailOtpScreen &&
      !options.isArkoseScreen &&
      !options.isSignupPage
    ) {
      setPendingSignupEmail(undefined);
      setAutoSubmitPendingSignupEmail(false);
    }
  };

  return {
    ...decision,
    pendingSmsPhone,
    phoneSubmitInFlight,
    setPhoneSubmitInFlight,
    setPendingSmsPhone,
    pendingSignupEmail,
    hasPendingSignupEmail,
    autoSubmitPendingSmsPhone,
    setAutoSubmitPendingSmsPhone,
    autoSubmitPendingSignupEmail,
    setAutoSubmitPendingSignupEmail,
    onScreenUpdate,
  };
};
